import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import { contact } from '../lib/apiconfig';
import { toast } from 'sonner';
import { postApi } from '@/lib/apiHelper';

const WhatsAppIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const whatsappNumber = '917358481281';
  const whatsappMessage = encodeURIComponent('Hi, I\'d like to inquire about Luxtansa Resorts...');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    postApi(contact, formData).then((result) => {
      if (result.success) {
        toast.success(`Message Sent! ${result.message}`);

        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        toast.error(result.message || 'Failed to send message. Please try again.');
      }
    }).catch(() => {
      toast.error('Failed to send message. Please try again.');
    }).finally(() => {
      setLoading(false);
    });
  };

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Address',
      content: '4/1A3, Athanavur, Yelagiri Hills, Tirupattur Dist, 635853'
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      content: '+91 73584 81281'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'luxtansaresorts@gmail.com'
    },
    {
      icon: <Clock size={24} />,
      title: 'Reception Hours',
      content: '24/7 Available'
    },
    {
      icon: <WhatsAppIcon size={24} />,
      title: 'WhatsApp',
      content: 'Chat with us',
      isWhatsApp: true,
      link: whatsappUrl
    }
  ];

  return (
    <section id="contact" className="contact-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Have questions? We'd love to hear from you. Send us a message.
          </p>
        </div>

        <div className="contact-container">
          <div className="contact-info-cards">
            {contactInfo.map((info, index) => (
              info.isWhatsApp ? (
                <a 
                  key={index} 
                  href={info.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="contact-info-card contact-whatsapp-card"
                >
                  <div className="contact-info-icon contact-whatsapp-icon">{info.icon}</div>
                  <h4 className="contact-info-title">{info.title}</h4>
                  <p className="contact-info-content">{info.content}</p>
                </a>
              ) : (
                <div key={index} className="contact-info-card">
                  <div className="contact-info-icon">{info.icon}</div>
                  <h4 className="contact-info-title">{info.title}</h4>
                  <p className="contact-info-content">{info.content}</p>
                </div>
              )
            ))}
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="contact-name">Your Name *</Label>
                <Input id="contact-name" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Doe" required />
              </div>

              <div className="form-group">
                <Label htmlFor="contact-email">Email Address *</Label>
                <Input id="contact-email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" required />
              </div>
            </div>

            <div className="form-group mb-3">
              <Label htmlFor="contact-subject">Subject *</Label>
              <Input id="contact-subject" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="How can we help?" required />
            </div>

            <div className="form-group">
              <Label htmlFor="contact-message">Message *</Label>
              <Textarea id="contact-message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Your message..." rows={6} required />
            </div>

            <Button type="submit" className="contact-submit-button" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </Button>
          </form>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <h3 className="map-title">Find Us</h3>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3899.5!2d78.628008!3d12.582816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTLCsDM0JzU4LjEiTiA3OMKwMzcnNDAuOCJF!5e0!3m2!1sen!2sin!4v1702800000000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Luxtansa Resorts Location - Yelagiri Hills"
            ></iframe>
          </div>
          <div className="map-address">
            <MapPin size={20} />
            <span>4/1A3, Athanavur, Yelagiri Hills, Tirupattur Dist, 635853</span>
          </div>
        </div>
        
      </div>
    </section>
  );
};

export default Contact;
